/** @format */

import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { DraftQuestionInput } from "@question-bank/shared";
import { questionImportService, type ImportPaper } from "../services/questionImportService";
import type { CoreWarning } from "../features/csvImport/coreValidation";
import { formatSeason } from "../utils/helpers";
import { notify } from "./Toast/toast";
import { useState } from "react";
import { Locale } from "../features/i18n/init";
import { useBrand } from "../hooks/useBrand";

const buttonStyles = "cursor-pointer px-6 h-8 rounded-md font-semibold";

interface PreviewData {
	questions: DraftQuestionInput[];
	totalQuestions: number;
	paper: ImportPaper;
	coreWarnings: {
		paper: CoreWarning[];
		rows: { index: number; warnings: CoreWarning[] }[];
	};
}

export const CsvImportPreviewTable = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { brand } = useBrand();
	const [isImporting, setIsImporting] = useState(false);

	const { previewData } = (location.state || {}) as { previewData?: PreviewData };
	const { locale } = useParams<{ locale: Locale }>();

	if (!previewData) {
		return (
			<>
				<div>No preview data available</div>
				<Link
					to={`/${locale}/admin/import`}
					className="text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
				>
					← Back
				</Link>
			</>
		);
	}

	const { questions, totalQuestions, paper, coreWarnings } = previewData;
	// Keyed by position in the `questions` array, matching how the producer keys
	// them, so each warning lines up with its rendered row.
	const rowWarnings = new Map(coreWarnings.rows.map((entry) => [entry.index, entry.warnings]));

	const handleImport = async () => {
		if (!questions) {
			return;
		}

		setIsImporting(true);
		try {
			const job = await questionImportService.importQuestions(brand, paper, questions);
			const count = job.createdQuestionIds.length;
			notify("success", `${count} question${count === 1 ? "" : "s"} imported successfully`);
			navigate(`/${locale}/admin/import-jobs/${encodeURIComponent(job.id)}`);
		} catch (error) {
			notify("error", error instanceof Error ? error.message : "Import failed. Please try again");
		} finally {
			setIsImporting(false);
		}
	};

	return (
		<div>
			<h1 className="font-bold mb-4">Total questions to be imported: {totalQuestions} </h1>

			{/* Paper-level block — applies to every question in this import (DR8). */}
			<dl className="mb-4 grid grid-cols-2 gap-x-6 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-4">
				<div>
					<dt className="font-semibold text-slate-500">Paper code</dt>
					<dd className="text-slate-800">{paper.paperCode}</dd>
				</div>
				<div>
					<dt className="font-semibold text-slate-500">Subject</dt>
					<dd className="text-slate-800">{paper.subject}</dd>
				</div>
				<div>
					<dt className="font-semibold text-slate-500">Qualification</dt>
					<dd className="text-slate-800">{paper.qualification.title ?? paper.qualification.code}</dd>
				</div>
				<div>
					<dt className="font-semibold text-slate-500">Tier</dt>
					<dd className="text-slate-800">{paper.tier ? (paper.tier.title ?? paper.tier.code) : "—"}</dd>
				</div>
				<div>
					<dt className="font-semibold text-slate-500">Series</dt>
					<dd className="text-slate-800">
						{formatSeason(paper.season)} {paper.year}
					</dd>
				</div>
				<div>
					<dt className="font-semibold text-slate-500">Date</dt>
					<dd className="text-slate-800">{paper.date ? new Date(paper.date).toLocaleDateString("en-GB") : "—"}</dd>
				</div>
			</dl>

			{coreWarnings.paper.length > 0 && (
				<div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
					<h2 className="mb-1 font-bold text-amber-800">Paper-level Core API warnings</h2>
					<ul className="list-disc pl-5 text-sm text-amber-700">
						{coreWarnings.paper.map((warning, index) => (
							<li key={index}>{warning.message}</li>
						))}
					</ul>
				</div>
			)}

			<div className="overflow-x-auto overflow-y-auto max-h-screen rounded-lg border border-slate-200 bg-white shadow-(--admin-shadow-raised)">
				<table className="min-w-full divide-y divide-slate-200 text-left text-sm">
					<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
						<tr>
							<th scope="col" className="px-3 py-2">
								Question Number
							</th>
							<th scope="col" className="px-3 py-2">
								Marks
							</th>
							<th scope="col" className="px-3 py-2">
								Language
							</th>
							<th scope="col" className="px-3 py-2">
								Theme
							</th>
							<th scope="col" className="px-3 py-2">
								Topic
							</th>
							<th scope="col" className="px-3 py-2">
								Sub-Topic
							</th>
							<th scope="col" className="px-3 py-2">
								Core match
							</th>
						</tr>
					</thead>
					<tbody>
						{questions.map((row: DraftQuestionInput, index: number) => {
							const warnings = rowWarnings.get(index) ?? [];
							return (
								<tr key={row.questionNumber ?? index} className="transition-colors hover:bg-slate-50">
									<td className="px-4 py-3 text-slate-700">{row.questionNumber}</td>
									<td className="px-4 py-3 text-slate-700">{row.maxMarks}</td>
									<td className="px-4 py-3 text-slate-700">{row.language}</td>
									<td className="px-4 py-3 text-slate-700">{row.specification?.theme}</td>
									<td className="px-4 py-3 text-slate-700">{row.specification?.topic}</td>
									<td className="px-4 py-3 text-slate-700">{row.specification?.subTopic}</td>
									<td className="px-4 py-3">
										{warnings.length === 0 ? (
											<span className="text-green-700">✓ matched</span>
										) : (
											<span className="text-amber-700">{warnings.map((w) => w.message).join("; ")}</span>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			<div className="flex flex-row gap-4 mt-4 ">
				<button
					type="button"
					onClick={() => navigate(-1)}
					className={`${buttonStyles} bg-gray-200 text-gray-700 border border-gray-200`}
				>
					Cancel
				</button>
				<button
					type="button"
					className={`${buttonStyles}  bg-white border border-(--admin-color-primary-strong) text-(--admin-color-primary-strong)`}
				>
					Save & resume later
				</button>
				<button
					type="button"
					onClick={() => void handleImport()}
					disabled={isImporting}
					className={`${buttonStyles} bg-(--admin-color-primary-strong) border border-(--admin-color-primary-strong) text-white`}
				>
					{isImporting ? "Importing..." : "Import"}
				</button>
			</div>
		</div>
	);
};
